/**
 * World-map schema (zod) — the data-driven contract for the level-select atlas.
 *
 * The map is a vellum chart of the Mythic Pantheons pilgrimage: a set of realm
 * REGIONS, each themed to a pantheon, holding 2–3 STOP nodes the player travels
 * through. Nodes are joined into a single chain by `prereq` (a node id), which
 * the map draws as a dotted treasure-map route and which drives unlock order.
 *
 * Coordinates are map-space px in the atlas plate (see MAP below). A node with
 * `levelId: null` is a sealed/unbuilt stop — it shows on the map under fog but
 * can't be embarked yet. Validating on load means bad map data fails fast with a
 * clear error, exactly like the level/unit registries.
 */
import { z } from 'zod';

/** Atlas dimensions (matches the painted `public/assets/ui/world_map.jpg`,
 *  pannable in the 720×1280 viewport). Both the painted plate and the SVG
 *  fallback are stretched to this size, so node coords are CANONICAL here —
 *  they line up on either backdrop. */
export const MAP = { width: 1440, height: 2912 } as const;

export const RegionSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** The pantheon realm key (olympus/duat/helheim/yomi/xibalba/unmade). */
  realm: z.string(),
});

export const MapNodeSchema = z.object({
  id: z.string(),
  /** The level this stop launches; null = sealed/unbuilt ("coming soon"). */
  levelId: z.string().nullable().default(null),
  regionId: z.string(),
  name: z.string(),
  /** One-line flavor shown in the node info panel. */
  flavor: z.string().default(''),
  /** Map-space position of the node medallion. */
  x: z.number(),
  y: z.number(),
  /** Node id that must be cleared (tier ≥ 1) before this one unlocks. The chain
   *  root has `prereq: null` and is unlocked from the start. */
  prereq: z.string().nullable().default(null),
});

export const WorldMapSchema = z.object({
  regions: z.array(RegionSchema).min(1),
  nodes: z.array(MapNodeSchema).min(1),
});

export type Region = z.infer<typeof RegionSchema>;
export type MapNode = z.infer<typeof MapNodeSchema>;
export type WorldMap = z.infer<typeof WorldMapSchema>;

/** Parse + validate raw world-map JSON, throwing a descriptive error on bad
 *  data. Also checks referential integrity (region ids + prereq ids resolve) so
 *  a typo in the chain surfaces at load, not as a silently-broken map. */
export function parseWorldMap(raw: unknown): WorldMap {
  const result = WorldMapSchema.safeParse(raw);
  if (!result.success) {
    throw new Error(`Invalid world-map data:\n${result.error.toString()}`);
  }
  const map = result.data;
  const regionIds = new Set(map.regions.map((r) => r.id));
  const nodeIds = new Set(map.nodes.map((n) => n.id));
  for (const n of map.nodes) {
    if (!regionIds.has(n.regionId)) {
      throw new Error(`World-map node ${n.id}: unknown regionId "${n.regionId}"`);
    }
    if (n.prereq && !nodeIds.has(n.prereq)) {
      throw new Error(`World-map node ${n.id}: unknown prereq "${n.prereq}"`);
    }
  }
  return map;
}
